import { ReactElement } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { font, space, color } from '@op/opds.tokens';
import { Message } from '@messageformat/react';
import { IconInformation } from '@op/opds.opux-icons/indicator';
import { Tile, Row, List, ListItem, uniqueId } from '@op/opds.opux';
import { ReactComponent as Circle } from '../../../images/circle.svg';
import { ReactComponent as Rectangle } from '../../../images/rectangle.svg';
import GraphSpecs from './GraphSpecs';

interface Props {
  options: Object;
  text: {
    title: string;
    subtitle: string;
    estimatedSave: string;
    acccountSaving?: string;
    sources?: string;
    altText: string;
  };
  specs: {
    investmentAmount: number;
    accountSavings?: number;
    profitExpected: number;
    investmentTime: number;
  };
}

const InterestGraphBase = (props: Props): ReactElement => {
  const { text, options, specs } = props;
  const { investmentAmount, accountSavings, profitExpected, investmentTime } = specs;
  const altTextId = uniqueId('interest-graph-alt-text-');
  return (
    <div className="investoracademy-graph-wrapper">
      <Tile>
        <h4 className="investoracademy-general-graph-title">
          {text.title && <Message id={text.title} />}
        </h4>
        {text.subtitle && (
          <p className="investoracademy-general-graph-description">
            <Message id={text.subtitle} />
          </p>
        )}
        <GraphSpecs
          investmentAmount={investmentAmount}
          accountSavings={accountSavings}
          profitExpected={profitExpected}
          investmentTime={investmentTime}
        />
        <div style={{ marginTop: space.small }}>
          <Row>
            <div style={{ display: 'none' }} id={altTextId}>
              <Message id={text.altText} />
            </div>
            <div
              style={{ flex: 1, width: '100%' }}
              role="img"
              aria-labelledby={altTextId}
            >
              <HighchartsReact highcharts={Highcharts} options={options} />
              <div className="investoracademy-investorgraph-label-container">
                <p className="align-center" style={{ fontSize: font.size.body.xs }}>
                  <Message id="course2.part3.interest_example_graph.zero_year" />
                </p>
                <p className="align-center" style={{ fontSize: font.size.body.xs }}>
                  <Message id="course2.part3.interest_example_graph.ten_year" />
                </p>
                <p className="align-center" style={{ fontSize: font.size.body.xs }}>
                  <Message id="course2.part3.interest_example_graph.twenty_year" />
                </p>
                {investmentTime === 30 && (
                  <p className="align-center" style={{ fontSize: font.size.body.xs }}>
                    <Message id="course2.part3.interest_example_graph.thirty_year" />
                  </p>
                )}
              </div>
            </div>
          </Row>
          <Row>
            <div style={{ textAlign: 'center', margin: '0 auto' }}>
              <List
                border="none"
                size="tiny"
                indented
                style={{ display: 'inline-block', textAlign: 'left', margin: '0' }}
              >
                <ListItem style={{ display: 'inline' }}>
                  <div className="investoracademy-listitem-container">
                    <Circle fill={color.indicator.default} aria-hidden="true" />
                    <Message id={text.estimatedSave} />
                  </div>
                </ListItem>
                {text?.acccountSaving && (
                  <ListItem style={{ display: 'inline' }}>
                    <div className="investoracademy-listitem-container">
                      <Rectangle fill={color.icon.default} aria-hidden="true" />
                      <Message id={text.acccountSaving} />
                    </div>
                  </ListItem>
                )}
              </List>
            </div>
          </Row>
        </div>
      </Tile>
      {text?.sources && (
        <p
          className="opds-styles-v1-text-label-small"
          style={{ display: 'flex', paddingBottom: space.default, textAlign: 'left' }}
        >
          <IconInformation />
          {text.sources && <Message id={text.sources} />}
        </p>
      )}
    </div>
  );
};

export default InterestGraphBase;
